/**
 * Created by Gaurav on Oct/15/2019.
 */

import { IGameConfig } from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import { IDeviceDependentConfig } from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import { IGameTweakerConfig } from "@nolimitcity/slot-game/bin/dev/tweaker/GameTweaker";
import { IWinRatios, WinRatioType } from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import { BetWinMode } from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import { BarbarianFuryGameAssets } from "./BarbarianFuryGameAssets";
import { BarbarianFuryLayerConfig, IBarbarianFuryLayerConfig } from "./stage/IBarbarianFuryLayerConfig";
import { LayerScaleOption } from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import { Back, Ease } from "gsap";
import { IBarbarianFurySymbolStates } from "./symbols/BarbarianFuryReelSymbol";
import { IStackedReelSymbolsConfig } from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import { SpinMode } from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";

export class BarbarianFuryGameConfig implements IGameConfig {

    public static instance: BarbarianFuryGameConfig = new BarbarianFuryGameConfig();

    //some game level vars
    BROWSER_FPS: number = 30;
    PERFORMANCE_LOGGER: boolean = true;
    BET_WIN_MODE: BetWinMode = BetWinMode.BET_LINE;
    SPIN_MODE: SpinMode = SpinMode.SPIN;

    //var for game height & width
    GAME_WIDTH: number = 1280;
    GAME_HEIGHT: number = 720;

    //var for symbols
    SYMBOL_WIDTH: number = 144;
    SYMBOL_HEIGHT: number = 149;
    SYMBOL_BOTTOM_PADDING: number = 0;
    SYMBOLS_NUM_PER_REEL: number = 4;

    SYMBOLS_NUM_IN_REEL: [number, number, number, number, number] = [3, 1, 4, 1, 0];

    SYMBOLS_ALL: string[] = ["M1", "M2", "M3", "W", "L1", "L2", "L3", "L4", "L5"]; //ket 2 different array because in "SYMBOLS_ALL", we can add Scatter if required
    SYMBOLS_WITH_IDLE_SPINE: string[] = ["M1", "M2", "M3", "W", "L1", "L2", "L3", "L4", "L5"];
    SYMBOLS_WITH_WIN_SPINE: string[] = ["M1", "M2", "M3"];

    STACKED_SYMBOLS: IStackedReelSymbolsConfig = {
        "W": {
            totalNum: 4,
            reels: [1, 2, 3]
        },
        "M1": {
            totalNum: 4
        },
        "M2": {
            totalNum: 4
        },
        "M3": {
            totalNum: 4
        }
    };
    //var for reels
    REELS_NUM: number = 5;
    SPACE_BETWEEN_REELS: number = 0;
    REEL_AREA_SIDE_MARGINS = 0;
    REEL_AREA_TOP_MARGINS = 0;
    REEL_BG_WIDTH: number = 795; //836 - this.SPACE_BETWEEN_REELS - this.REEL_AREA_SIDE_MARGINS;
    REEL_BG_HEIGHT: number = 755;
    REEL_AREA_WIDTH: number = 720;
    REEL_AREA_HEIGHT: number = 595;
    REEL_AREA_POS_X: number = 0;
    REEL_AREA_POS_Y: number = 85;
    REEL_DISPLAY_POSITIONS: [number, number][] = [
        [this.REEL_AREA_SIDE_MARGINS + (this.SYMBOL_WIDTH * 0.5), this.REEL_AREA_TOP_MARGINS],
        [this.REEL_AREA_SIDE_MARGINS + (this.SYMBOL_WIDTH * 1.5) + this.SPACE_BETWEEN_REELS, this.REEL_AREA_TOP_MARGINS],
        [this.REEL_AREA_SIDE_MARGINS + (this.SYMBOL_WIDTH * 2.5) + (this.SPACE_BETWEEN_REELS * 2), this.REEL_AREA_TOP_MARGINS],
        [this.REEL_AREA_SIDE_MARGINS + (this.SYMBOL_WIDTH * 3.5) + (this.SPACE_BETWEEN_REELS * 3), this.REEL_AREA_TOP_MARGINS],
        [this.REEL_AREA_SIDE_MARGINS + (this.SYMBOL_WIDTH * 4.5) + (this.SPACE_BETWEEN_REELS * 4), this.REEL_AREA_TOP_MARGINS]
    ];
    //layer scaling options for desktop, mobile - landscape & portrait mode
    DEFAULT_LAYER_SCALE_OPTION: IDeviceDependentConfig<LayerScaleOption> = BarbarianFuryLayerConfig.defaultLayerScaleOptions;
    //pixi layer layout order
    LAYERS: IBarbarianFuryLayerConfig = BarbarianFuryLayerConfig.getConfig();
    // Reel Spin timing related
    REEL_SPIN_TIME: number = 0.7;
    MIN_STOPPING_TIME: number = 3; // #169 => check class jurisdiction-se.js and SlotApiEventManager.js
    REEL_FAST_SPIN_SPIN_TIME: number = 0.1;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION: number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY: number = 0.3;
    REEL_SPIN_START_EASE: Ease = Back.easeIn.config(4);
    REEL_STOP_DELAY: number = 0.3;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION: number = 0.01;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY: number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG: [number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG: [number, number] = [1.2, 0.75];
    REEL_SPIN_START_SPEED: number[] = [0.15, 0.25, 0.3, 0.35, 0.45];
    REEL_FAST_SPIN_START_SPEED: number = 0.3;
    REEL_SPIN_STOP_SPEED: number = 0.5;
    REEL_SPIN_SPIN_SPEED: number = 0.055;
    REEL_NEAR_WIN_SPIN_SPEED: number = 0.03;
    REEL_NEAR_WIN_STOP_DELAY: number = 3.0;
    REEL_SPIN_QUICK_STOP_SPEED: number = 0.04;
    REEL_QUICK_STOP_DELAY: number = 0.01;
    //fake reels related
    REEL_SETS: any = {
        "BASE_REELSET": [
            ["M1", "M1", "M1", "M1", "L4", "L4", "L1", "L1", "L2", "L2", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1",
                "L5", "L4", "L4", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1", "L4", "L4", "L1", "L1", "L5", "L3", "L3",
                "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L4", "L4", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "M3", "M3", "M3",
                "M3", "L1", "L1", "L4", "L4", "L3", "L3", "L2", "L2", "L1", "L1", "L4", "L4", "L3", "L3", "L4", "L4", "L1", "L1", "L2",
                "L2", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1", "L5", "L4", "L4", "L4", "L4", "S", "L1", "L1", "L1",
                "L1", "L4", "L4", "L1", "L1", "L5", "L3", "L3", "M3", "M3", "M3", "M3", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3",
                "L4", "L4", "L1", "L1", "L3", "L3", "L5", "L4", "L4", "L1", "L1", "L4", "L4", "L3", "L3", "L2", "L2", "L1", "L1", "L4",
                "L4", "L3", "L3"],
            ["M3", "M3", "M3", "M3", "L2", "L2", "L4", "L4", "S", "L3", "L3", "L4", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L2",
                "L2", "L1", "L5", "L5", "L1", "M1", "M1", "M1", "M1", "L1", "L5", "L4", "L4", "L2", "L2", "L4", "L4", "M2", "M2", "M2",
                "M2", "L4", "L4", "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3", "L2", "L2", "S", "L4", "L4", "L1", "L1", "M2", "M2",
                "M2", "M2", "L3", "L3", "L4", "L4", "L5", "L1", "L2", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L3", "L3", "L5", "L5",
                "L1", "L1", "M1", "M1", "M1", "M1", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3",
                "L4", "L4", "L2", "L2", "S", "L1", "L1", "L3", "L3", "L5", "W", "W", "W", "W", "L4", "L4", "L2", "L2", "L2", "L4",
                "L4", "L3", "M1", "M1", "M1", "M1", "L2", "L2", "L4", "L4", "L3", "L3", "M1", "M1", "M1", "M1", "L4", "L5", "L5", "L4",
                "S", "L2", "L2", "L1", "L1", "M1", "M1", "M1", "M1", "L2", "L2", "L4", "L4", "S", "L3", "L1", "L1", "L3", "M1", "M1",
                "M1", "M1", "L4", "L4", "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3", "L2", "L2", "S", "L4", "L4", "L1", "L1", "L5",
                "M1", "M1", "M1", "M1", "L3", "L3", "L4", "L4", "L2", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L1", "L1", "M1", "M1",
                "M1", "M1", "L2", "L2", "L4", "L4"],
            ["M1", "M1", "M1", "M1", "L3", "L3", "L2", "L1", "S", "L2", "L2", "L1", "L1", "L3", "L3", "M2", "M2", "M2", "M2", "L2",
                "L2", "L3", "L3", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "L4", "L3", "L2", "L4", "M3", "M3", "M3", "M3", "L1",
                "L1", "L3", "L3", "L5", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "L1", "L1", "L3", "L3", "L2", "L2",
                "M2", "M2", "M2", "M2", "L5", "L2", "L2", "L3", "L3", "S", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3",
                "L1", "L1", "W", "W", "W", "W", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "M2", "M2", "M2", "M2", "L2", "L2",
                "L4", "L4", "L3", "L3", "L5", "L1", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2",
                "L2", "L2", "L4", "L4", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2",
                "L4", "L4", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L2", "L1", "L3", "S", "L3", "L3", "L4", "L4", "L2", "L2", "M2",
                "M2", "M2", "M2", "L4", "L4", "L1", "L1", "M2", "M2", "M2", "M2", "L2", "L2", "L4", "L4", "L3", "L3", "S", "L2", "L2",
                "L4", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "L1", "L1", "L5", "L3", "L3", "M2", "M2", "M2", "M2", "L4",
                "L4", "L2", "L2"],
            ["M1", "M1", "M1", "M1", "L1", "L1", "L2", "L5", "S", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L2", "L2", "L1",
                "L1", "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L3", "L3", "L1", "L1", "L4", "L4", "M2", "M2", "M2", "M2", "L2",
                "L2", "L5", "L3", "L3", "S", "L4", "L5", "L1", "L1", "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1", "L2", "L2", "S",
                "L4", "L2", "L1", "L5", "M1", "M1", "M1", "M1", "L1", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L5", "L2",
                "L2", "L1", "L1", "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L5", "L1", "L4", "L3", "L3", "L1", "L1", "L4", "L4", "M2",
                "M2", "M2", "M2", "L2", "L2", "L3", "L3", "L3", "L2", "L2", "L3", "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1", "L2",
                "L2", "M1", "M1", "M1", "M1", "L1", "L1", "L4", "L4", "S", "L3", "L3", "L1", "L4", "M3", "M3", "M3", "M3", "L2", "L2",
                "L1", "L1", "S", "L2", "L3", "L3", "L1", "M2", "M2", "M2", "M2", "L4", "L4", "L3", "L3", "L1", "S", "L1", "L4", "L5",
                "L4", "M2", "M2", "M2", "M2", "L2", "L2", "L3", "L3", "L1", "L2", "L2", "L4", "M1", "M1", "M1", "M1", "L3", "L3", "L1",
                "L1", "L2", "L5", "L2", "L5", "M1", "M1", "M1", "M1", "L1", "L1", "L4", "L4", "W", "W", "W", "W", "L3", "L3", "L1",
                "L1", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "S", "L4", "L4", "L3", "L3", "M2", "M2", "M2", "M2", "L4",
                "L4", "L3", "L3", "L1", "S", "L1", "L4", "L4", "L5", "M2", "M2", "M2", "M2", "L2", "L2", "L3", "L3", "L2", "L2", "L4",
                "L5", "M1", "M1", "M1", "M1", "L3", "L3", "L2", "L2", "M1", "M1", "M1", "M1", "L1", "L1", "L3", "L5", "S", "L4", "L4",
                "L3", "L3", "M3", "M3", "M3", "M3", "L2", "L2", "L1", "L1", "S", "L3", "L3", "L5", "L1", "L3", "M2", "M2", "M2", "M2",
                "L4", "L4", "L3", "L3", "L1", "L1", "S", "L4", "L4", "L5", "L2", "M2", "M2", "M2", "M2", "L2", "L2", "L5", "L3", "L3",
                "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1", "S", "L2", "L2", "L1", "L1"],
            ["M1", "M1", "M1", "M1", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2",
                "M2", "M2", "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5", "S", "L5", "L4", "L4", "L3", "L3", "M3", "M3",
                "M3", "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3",
                "L5", "S", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L5", "L4", "L4", "L1", "L1",
                "L2", "L2", "L3", "L3", "L5", "S", "L5", "L4", "L4", "L3", "L3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2",
                "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5"]
        ],
        "FREESPIN_REELSET": [
            ["M1", "M1", "M1", "M1", "L4", "L4", "L1", "L1", "L2", "L2", "S", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1",
                "L4", "L4", "M2", "M2", "M2", "M2", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1", "L4", "L4", "L1", "L1",
                "L3", "L3", "M3", "M3", "M3", "M3", "L4", "L4", "L1", "L1", "S", "L2", "L2", "L3", "L3", "L4", "L4", "L1", "L1", "L3", "L3",
                "L4", "L4", "M1", "M1", "M1", "M1", "L1", "L1", "L4", "L4", "S", "L3", "L3", "L2", "L2", "L1", "L1", "L4", "L4", "L3", "L3",
                "M1", "M1", "M1", "M1", "L4", "L4", "L1", "L1", "L2", "L2", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1",
                "L4", "L4", "M2", "M2", "M2", "M2", "L4", "L4", "L1", "L1", "M3", "M3", "M3", "M3", "L1", "L1", "L4", "L4", "S", "L1",
                "L1", "L3", "L3", "M3", "M3", "M3", "M3", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L4", "L4", "L1", "L1", "L3",
                "L3", "L4", "L4", "M1", "M1", "M1", "M1", "L1", "L1", "L4", "L4", "S", "L3", "L3", "L2", "L2", "L1", "L1", "L4", "L4", "L3",
                "L3"],
            ["M3", "M3", "M3", "M3", "L2", "L2", "L4", "L4", "S", "L3", "L3", "L4", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L2",
                "L2", "L1", "L1", "M1", "M1", "M1", "M1", "L1", "L5", "L4", "L4", "W", "W", "W", "W", "L2", "L2", "L4", "L4", "M2", "M2", "M2", "M2", "L4",
                "L4", "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3", "L2", "L2", "L4", "L4", "L1", "L1", "M2", "M2", "M2", "M2", "L3",
                "L3", "L4", "L4", "L5", "L1", "L2", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L3", "L3", "S", "L5", "L5", "L1", "L1", "M1",
                "M1", "M1", "M1", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3", "L4", "L4", "L2",
                "L2", "L1", "L1", "L3", "L3", "L5", "W", "W", "W", "W", "L4", "L4", "L2", "L2", "L2", "L4", "L4", "L3", "M1", "M1",
                "M1", "M1", "L2", "L2", "L4", "L4", "M1", "M1", "M1", "M1", "L4", "L5", "L5", "L4", "L2", "L2", "L1", "L1", "M1", "M1",
                "M1", "M1", "L2", "L2", "L4", "L4", "S", "L3", "L1", "L1", "L3", "M1", "M1", "M1", "M1", "L4", "L4", "L2", "L2", "M1", "M1",
                "M1", "M1", "L3", "L3", "L2", "L2", "S", "L4", "L4", "L1", "L1", "L5", "M1", "M1", "M1", "M1", "L3", "L3", "L4", "L4", "L2",
                "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L1", "L1", "M1", "M1", "M1", "M1", "L2", "L2", "L4", "L4", "M3", "M3", "M3",
                "M3", "L2", "L2", "L4", "L4", "W", "W", "W", "W", "L3", "L3", "L4", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L2", "L2", "S", "L1", "L5",
                "L5", "L1", "M1", "M1", "M1", "M1", "L1", "L5", "L4", "L4", "L2", "L2", "L4", "L4", "M2", "M2", "M2", "M2", "L4", "L4",
                "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3", "L2", "L2", "L4", "L4", "L1", "L1", "M2", "M2", "M2", "M2", "L3", "L3",
                "L4", "L4", "L5", "L1", "L2", "L2", "M1", "M1", "M1", "M1", "L4", "L4", "L3", "L3", "L5", "L5", "L1", "L1", "M1", "M1",
                "M1", "M1", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "M1", "M1", "M1", "M1", "L3", "L3", "L4", "L4", "L2", "L2",
                "L1", "L1", "L3", "L3", "L5", "W", "W", "W", "W", "L4", "L4", "L2", "L2", "L2", "L4", "L4", "L3", "M1", "M1", "M1",
                "M1", "L2", "L2", "L4", "L4", "M1", "M1", "M1", "M1", "L4", "L5", "L5", "L4", "S", "L2", "L2", "L1", "L1", "M1", "M1", "M1",
                "M1", "L2", "L2", "L4", "L4", "L3", "L1", "M1", "M1", "M1", "M1", "L4", "L4", "L2", "L2", "M1", "M1", "M1", "M1", "L3",
                "L3", "L2", "L2", "W", "W", "W", "W", "L4", "L4", "L1", "L1", "L5", "M1", "M1", "M1", "M1", "L3", "L3", "L4", "L4", "L2", "L2", "M1", "M1",
                "M1", "M1", "L4", "L4", "L1", "L1", "M1", "M1", "M1", "M1", "L2", "L2", "L4", "L4"],
            ["M1", "M1", "M1", "M1", "L3", "L3", "L2", "L1", "L2", "L2", "L1", "L1", "L3", "L3", "M2", "M2", "M2", "M2", "L2", "L2",
                "L3", "L3", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "S", "L4", "L3", "L2", "L4", "M3", "M3", "M3", "M3", "L1", "L1",
                "L3", "L3", "L5", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "L1", "L1", "L3", "L3", "L2", "L2", "M2",
                "M2", "M2", "M2", "L5", "L2", "L2", "L3", "L3", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L1", "L1",
                "W", "W", "W", "W", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "M2", "M2", "M2", "M2", "L2", "L2", "L4", "L4",
                "L3", "L3", "L5", "L1", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L2", "L2",
                "L4", "L4", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "W", "W", "W", "W", "L4", "L4",
                "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L2", "L1", "L3", "L3", "S", "L3", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2",
                "L4", "L4", "L1", "L1", "M2", "M2", "M2", "M2", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "L4", "L3", "M2", "M2",
                "M2", "M2", "L4", "L4", "L2", "L2", "L1", "L1", "L5", "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "M1",
                "M1", "M1", "M1", "L3", "L3", "L2", "L1", "S", "L2", "L2", "L1", "L1", "L3", "L3", "M2", "M2", "M2", "M2", "L2", "L2",
                "L3", "L3", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "W", "W", "W", "W", "L4", "L3", "L2", "L4", "M3", "M3", "M3", "M3", "L1", "L1",
                "L3", "L3", "L5", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "L1", "L1", "L3", "L3", "L2", "L2", "M2",
                "M2", "M2", "M2", "L5", "L2", "L2", "L3", "L3", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L1", "L1",
                "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "M2", "M2", "M2", "M2", "L2", "L2", "L4", "L4", "W", "W", "W", "W", "L3", "L3", "L5", "L1",
                "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L2", "L2", "L4", "L4", "M2", "M2",
                "M2", "M2", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2", "L3", "L3", "L2", "L2", "L4", "L4", "L1", "L1", "M2", "M2",
                "M2", "M2", "L5", "L2", "L1", "L3", "W", "W", "W", "W", "L3", "L3", "L4", "L4", "L2", "L2", "M2", "M2", "M2", "M2",
                "L4", "L4", "L1", "L1", "M2", "M2", "M2", "M2", "L2", "L2", "L4", "L4", "L3", "L3", "L2", "L2", "L4", "L3", "M2", "M2",
                "M2", "M2", "L4", "L4", "L2", "L2", "L1", "L1", "L5", "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L2", "L2"],
            ["M1", "M1", "M1", "M1", "L1", "L1", "L2", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L2", "L2", "L1", "L1",
                "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L3", "L3", "L1", "L1", "L4", "L4", "M2", "M2", "M2", "M2", "L2", "L2",
                "L5", "L3", "L3", "L4", "L5", "L1", "L1", "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1", "W", "W", "W", "W", "L2", "L2", "L4", "L2", "L1",
                "L5", "M1", "M1", "M1", "M1", "L1", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L5", "L2", "L2", "L1", "L1",
                "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L5", "L1", "L4", "L3", "L3", "L1", "L1", "L4", "L4", "M2", "M2", "M2", "M2",
                "L2", "L2", "L3", "L3", "W", "W", "W", "W", "L3", "L2", "L2", "L3", "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1",
                "L2", "L2", "M1", "M1", "M1", "M1", "L1", "L1", "L4", "L4", "S", "L3", "L3", "L1", "L4", "M3", "M3", "M3", "M3", "L2", "L2",
                "L1", "L1", "L2", "L3", "L3", "L1", "M2", "M2", "M2", "M2", "L4", "L4", "L3", "L3", "L1", "L1", "L4", "L5", "L4", "M2",
                "M2", "M2", "M2", "L2", "L2", "L3", "L3", "L1", "L2", "L2", "L4", "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1", "L2",
                "L5", "L2", "L5", "M1", "M1", "M1", "M1", "L1", "L1", "L4", "L4", "L3", "L3", "L1", "L1", "M3", "M3", "M3", "M3", "L5",
                "L2", "L2", "L1", "L1", "L4", "L4", "L3", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L3", "L3", "L1", "S", "L1", "L4",
                "L4", "L5", "M2", "M2", "M2", "M2", "L2", "L2", "L3", "L3", "S", "L2", "L2", "L4", "L5", "M1", "M1", "M1", "M1", "L3", "L3",
                "L2", "L2", "M1", "M1", "M1", "M1", "L1", "L1", "L3", "L5", "W", "W", "W", "W", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L2", "L2",
                "L1", "L1", "L3", "L3", "L5", "L1", "L3", "M2", "M2", "M2", "M2", "L4", "L4", "L3", "L3", "L1", "L1", "L4", "L4", "L5",
                "L2", "M2", "M2", "M2", "M2", "L2", "L2", "L5", "L3", "L3", "M1", "M1", "M1", "M1", "L3", "L3", "L1", "L1", "L2", "L2",
                "L1", "L1"],
            ["M1", "M1", "M1", "M1", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2",
                "M2", "M2", "L5", "L4", "L4", "L1", "L1", "S", "L2", "L2", "L3", "L3", "L5", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3",
                "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4", "L4", "L1", "L1", "S", "L2", "L2", "L3", "L3", "L5",
                "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "L5", "L4", "L4", "L1", "L1", "L2",
                "L2", "L3", "L3", "L5", "L5", "S", "L4", "L4", "L3", "L3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4",
                "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5", "M1", "M1", "M1", "M1", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3",
                "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5",
                "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4",
                "L4", "L1", "L1", "L2", "L2", "S", "L3", "L3", "L5", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2",
                "L1", "L1", "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5", "L5", "L4", "L4", "L3", "L3", "L5", "L2", "L2",
                "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4", "L4", "L1", "L1", "S", "L2", "L2", "L3", "L3", "L5", "M1", "M1", "M1", "M1",
                "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4",
                "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5", "L5", "L4", "L4", "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2",
                "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3", "L5", "S", "L5", "L4", "L4",
                "L3", "L3", "M3", "M3", "M3", "M3", "L5", "L2", "L2", "L1", "L1", "L5", "L4", "L4", "L1", "L1", "L2", "L2", "L3", "L3",
                "L5", "L5", "L4", "L4", "L3", "L3", "L5", "L2", "L2", "L1", "L1", "M2", "M2", "M2", "M2", "L5", "L4", "L4", "L1", "L1",
                "L2", "L2", "L3", "L3", "L5"]
        ],
        "STACKED_RESPIN_REELSET": [
            ["M1", "M1", "M1", "M1", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3"],
            ["M1", "M1", "M1", "M1", "L4", "L5", "L1", "L3", "L5", "L4", "L1", "L2", "L3", "L5", "L5", "L2", "L2", "L3", "L5", "L4",
                "L3", "L2", "L4", "L5"],
            ["M1", "M1", "M1", "M1", "L1", "L1", "L2", "L3", "L4", "L5", "L5", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L1", "L5",
                "L5", "L2", "L3", "L5"],
            ["M1", "M1", "M1", "M1", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L4", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L2", "L2", "L3", "L5", "L4",
                "L1", "L2", "L3", "L2", "L5", "L2", "L2", "L3", "L1", "L4", "L3", "L2", "L4", "L5", "L5", "L5", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L1", "L5", "L5", "L2", "L3", "L5"],
            ["M1", "M1", "M1", "M1", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L1", "L2", "L3", "L2", "L5", "L2", "L2", "L3", "L1", "L4", "L3", "L2", "L4", "L5", "L5",
                "L5", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L1", "L5", "L5", "L2", "L3", "L5"]
        ],
        "STACKED_RESPIN_FS_REELSET": [
            ["M1", "M1", "M1", "M1", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3"],
            ["M1", "M1", "M1", "M1", "L4", "L5", "L1", "L3", "L5", "L4", "L1", "L2", "L3", "L5", "L5", "L2", "L2", "L3", "L5", "L4",
                "L3", "L2", "L4", "L5"],
            ["M1", "M1", "M1", "M1", "L1", "L1", "L2", "L3", "L4", "L5", "L5", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L1", "L5",
                "L5", "L2", "L3", "L5"],
            ["M1", "M1", "M1", "M1", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L4", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L2", "L2", "L3", "L5", "L4",
                "L1", "L2", "L3", "L2", "L5", "L2", "L2", "L3", "L1", "L4", "L3", "L2", "L4", "L5", "L5", "L5", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L1", "L5", "L5", "L2", "L3", "L5"],
            ["M1", "M1", "M1", "M1", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L5", "L4",
                "L3", "L2", "L3", "L5", "L4", "L1", "L2", "L3", "L2", "L5", "L2", "L2", "L3", "L1", "L4", "L3", "L2", "L4", "L5", "L5",
                "L5", "L2", "L3", "L5", "L4", "L3", "L2", "L3", "L1", "L5", "L5", "L2", "L3", "L5"]
        ]
    };
    // different win timings
    WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                text: "Tiny Win",
                duration: 0,
                type: WinRatioType.TINY
            },
            {
                ratio: 1.5,
                text: "Small Win",
                duration: 0,
                type: WinRatioType.SMALL
            },
            {
                ratio: 3,
                text: "Medium Win",
                duration: 0.8,
                type: WinRatioType.MEDIUM
            },
            {
                ratio: 5,
                text: "Large Win",
                duration: 1.2,
                type: WinRatioType.LARGE
            }
        ],
        bigWin: [
            {
                ratio: 15,
                text: "Big Win",
                duration: 4.048,
                type: WinRatioType.BIG_WIN
            },
            {
                ratio: 25,
                text: "Mega Win",
                duration: 3.810,
                type: WinRatioType.MEGA_WIN

            },
            {
                ratio: 50,
                text: "Super Mega Win",
                duration: 3.610,
                type: WinRatioType.SUPER_MEGA_WIN
            }
        ]
    };
    GAME_TWEAKER_CONFIG: IGameTweakerConfig = {
        layer: this.LAYERS.TWEAK_MODULE.name,
        baseWindowWidth: 400,
        moduleHeight: 32,
        copyAllButtonText: "Copy All",
        buttonSkinIdle: "button",
        buttonSkinOver: "button",
        buttonSkinDown: "button",
        buttonSkinDisabled: "button-disabled"
    };

    private constructor() {
        if (BarbarianFuryGameConfig.instance) {
            throw new Error("BarbarianFuryGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }

    OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS: (symName: string, stateKey: string) => [number, number] = (symName: string, stateKey: string) => {
        if (symName === "S") {
            return [0, 0];
        } else if (symName === "M1") {
            if (stateKey === "idle" || stateKey === "initialWin") {
                return [-3.5, (this.SYMBOL_HEIGHT / 2) + 0.5];
            } else {
                return [0, 0];
            }
        } else if (symName === "M2") {
            if (stateKey === "idle" || stateKey === "initialWin") {
                return [-2.5, this.SYMBOL_HEIGHT - 19.5];
            } else {
                return [0, 0];
            }
        } else if (symName === "M3") {
            if (stateKey === "idle" || stateKey === "initialWin") {
                return [-2, this.SYMBOL_HEIGHT - 22];
            } else {
                return [0, 0];
            }
        } else if (symName === "W") {
            if (stateKey === "idle") {
                return [2.5, (this.SYMBOL_HEIGHT / 2) + 2.5];
            } else {
                return [0, 0];
            }
        } else if (symName === "L1") {
            if (stateKey === "idle") {
                return [-2, -2];
            } else {
                return [0, 0];
            }
        } else if (symName === "L2") {
            if (stateKey === "idle") {
                return [0, -0.5];
            } else {
                return [0, 0];
            }
        } else if (symName === "L3") {
            if (stateKey === "idle") {
                return [0, -7.5];
            } else {
                return [0, 0];
            }
        } else if (symName === "L4") {
            if (stateKey === "idle") {
                return [2, -4];
            } else {
                return [0, 0];
            }
        } else if (symName === "L5") {
            if (stateKey === "idle") {
                return [-2, -4];
            } else {
                return [0, 0];
            }
        } else {
            return [0, 0];
        }
    };

    //symbol states
    SYMBOL_STATES: IBarbarianFurySymbolStates = {
        normal: {
            prio: 0,
            keyword: "normal",
            texturesCreator: (symName: string, stateKey: string) => {
                return BarbarianFuryGameAssets.getSymbolStateTextures(symName);
            },
            offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        },
        spin: {
            prio: 1,
            keyword: "spin",
            texturesCreator: (symName: string, stateKey: string) => {
                return BarbarianFuryGameAssets.getSymbolStateTextures(symName, stateKey);
            },
            offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        },
        noWin: {
            prio: 2,
            keyword: "noWin",
            texturesCreator: (symName: string, stateKey: string) => {
                return BarbarianFuryGameAssets.getSymbolStateTextures(symName);
            },
            offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        },
        initialWin: {
            prio: 2,
            keyword: "initialWin",
            texturesCreator: (symName: string, stateKey: string) => {
                return BarbarianFuryGameAssets.getSymbolStateTextures(symName, stateKey);
            },
            offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        },
        win: {
            prio: 3,
            keyword: "win",
            texturesCreator: (symName: string, stateKey: string) => {
                return BarbarianFuryGameAssets.getSymbolStateTextures(symName);
            },
            offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        },
        hidden: {
            prio: 4,
            keyword: "hidden"
        },
        idle: {
            prio: 5,
            keyword: "idle",
            texturesCreator: (symName: string, stateKey: string) => {
                return BarbarianFuryGameAssets.getSymbolStateTextures(symName, stateKey);
            },
            offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        }
    };

    // #315
    WIN_BELOW_STAKE: boolean = false;
}